'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AVATAR_COLORS, client as clientLookup, PLANNING_STATUS, type ClientNr, type StatusKey } from '../demo-data'

import { useVisualCopy } from '../visual-copy'
import styles from './app-screen.module.css'

/**
 * Shared app-window frame for the home page's "real app" visuals (DP2a+). Carries the
 * app-UI color tokens (light + dark, see app-screen.module.css) that every visual inside
 * it references via `var(--au-*)`. Render each visual as:
 *
 *   <AppUiVisual label="…"><AppScreen>…end-state markup…</AppScreen></AppUiVisual>
 */
export function AppScreen({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={clsx(
        styles.root,
        'relative flex h-full w-full flex-col overflow-hidden rounded-[10px] border shadow-sm',
        'border-[color:var(--au-border)] bg-[var(--au-surface)] text-[var(--au-text)]',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The title bar every app visual opens with: a muted section name, a slash, and the accent-coloured
 * thing you are looking at ("Maandplanning / Juli 2026"), with optional stats on the right.
 *
 * `size` picks the scale, not the design: `hero` is the full-size home-hero treatment and `card` is
 * the same bar shrunk for the smaller in-page visuals. Keeping both in one component is what stops
 * the 20-odd visuals from drifting apart again.
 */
export function AppHeader({
  title,
  detail,
  detailClassName,
  right,
  leading,
  size = 'card',
  className,
}: {
  title: ReactNode
  /** The accent-coloured part after the slash. Omit for a bare title. */
  detail?: ReactNode
  /** Stats or controls pinned to the right — usually a `HeaderStat`. */
  right?: ReactNode
  /** Applied to the detail and the slash before it, so a card can drop the detail at phone width
   *  when what it puts on the right of the header needs the room more. */
  detailClassName?: string
  /** Icon rendered before the title, e.g. the AI sparkle. */
  leading?: ReactNode
  size?: 'hero' | 'card'
  className?: string
}) {
  return (
    <div className={clsx(styles.header, size === 'hero' ? styles.headerHero : styles.headerCard, className)}>
      {leading && <span className={styles.headerLeading}>{leading}</span>}
      <div className={styles.headerTitle}>
        {title}
        {detail !== undefined && (
          <>
            <span className={clsx(styles.headerSlash, detailClassName)}> / </span>
            <span className={clsx(styles.headerDetail, detailClassName)}>{detail}</span>
          </>
        )}
      </div>
      {right && <div className={styles.headerRight}>{right}</div>}
    </div>
  )
}

/**
 * The common right-hand header stat: a bold accent value with a muted label ("306 klanten"). It
 * scales itself from the `--stat-*` custom properties `AppHeader` sets, so it is automatically
 * hero-sized inside a hero header and card-sized inside a card header.
 */
export function HeaderStat({ value, label }: { value: ReactNode; label?: ReactNode }) {
  return (
    <div className={styles.stat}>
      <b className={styles.statValue}>{value}</b>
      {label && <span className={styles.statLabel}> {label}</span>}
    </div>
  )
}

/**
 * The small uppercase caption above a column or card of rows ("Per medewerker", "Checklist"). The
 * column-level counterpart to `FieldLabel`; use it wherever a block of rows is labelled, so those
 * captions — and the gap between a caption and the rows under it — cannot drift between visuals.
 *
 * `right` puts a figure on the same line (a count, a total). It goes through this component rather
 * than a caller-built row so that the caption-to-content gap stays in one place either way.
 */
export function ColumnLabel({ children, right }: { children: ReactNode; right?: ReactNode }) {
  if (right === undefined) return <span className={styles.columnLabel}>{children}</span>
  return (
    <div className={styles.columnLabelRow}>
      <span className={styles.columnLabelText}>{children}</span>
      {right}
    </div>
  )
}

export type SubHeaderColumn = {
  label: ReactNode
  /** Percentage complete — renders a progress track under the label when set. */
  pct?: number
  /**
   * Where the heading sits in its column. Always match the column's own content: a heading centred
   * over right-aligned values reads as a different column than the one it names.
   */
  align?: 'left' | 'center' | 'right'
  /** Extra class on this column, e.g. to hide it on narrow boards. */
  className?: string
}

/**
 * The column-header strip under `AppHeader`: a left-aligned row label ("Klant") followed by
 * centred, uppercase activity columns, each optionally carrying a progress track. One component so
 * every board's column strip has identical type, spacing and rules.
 */
export function AppSubHeader({
  rowLabel,
  columns,
  size = 'card',
  className,
  style,
}: {
  /** The left-most, row-describing label. */
  rowLabel: ReactNode
  columns: readonly SubHeaderColumn[]
  size?: 'hero' | 'card'
  className?: string
  /** Grid template etc — each board sets its own column widths. */
  style?: React.CSSProperties
}) {
  // Columns carrying a progress track are two lines tall, and the row label is lifted to sit on the
  // same line as their labels rather than in the middle of the strip.
  //
  // Only when EVERY column carries one. On a mixed strip — the year wall has RE. and MAAND between
  // the client column and the four activity columns — lifting the row label put KLANT three pixels
  // above two headings it sits in a row with, which is what the eye compares it to.
  const hasTrack = columns.length > 0 && columns.every((column) => column.pct !== undefined)
  return (
    <div
      className={clsx(
        styles.subHeader,
        size === 'hero' ? styles.subHeaderHero : styles.subHeaderCard,
        hasTrack && styles.subHeaderTracked,
        className,
      )}
      style={style}
    >
      <span className={styles.subHeaderRowLabel}>{rowLabel}</span>
      {columns.map((column, index) => (
        <div
          key={index}
          className={clsx(
            styles.colHead,
            column.align === 'left' && styles.colHeadLeft,
            column.align === 'right' && styles.colHeadRight,
            column.className,
          )}
        >
          <span className={styles.colLabel}>{column.label}</span>
          {column.pct !== undefined && (
            <div className={styles.track}>
              <div className={styles.trackFill} style={{ width: `${column.pct}%` }} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/** Centered modal overlay used by visuals that show an open dialog resting on top of a screen. */
export function ModalScrim({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={clsx('absolute inset-0 z-10 flex items-center justify-center p-3', className)}
      style={{ background: 'var(--au-scrim)' }}
    >
      {children}
    </div>
  )
}

export type ChipTone = 'done' | 'busy' | 'recv' | 'hatch'

const CHIP_BG: Record<ChipTone, string> = {
  done: 'var(--au-cell-done)',
  busy: 'var(--au-busy)',
  recv: 'var(--au-recv)',
  hatch: 'var(--au-hatch)',
}

/** Small colored status chip for grid cells (planning boards, etc). White text on colored
 * backgrounds, muted `--au-text2` on the neutral `hatch` background (empty or pending cells). */
export function StatusChip({
  tone,
  label,
  extra,
  className,
}: {
  tone: ChipTone
  label: string
  extra?: string
  className?: string
}) {
  const colored = tone !== 'hatch'
  return (
    <span
      className={clsx('flex items-center justify-center gap-0.5 rounded-[5px] px-1 py-1', className)}
      style={{ background: CHIP_BG[tone], color: colored ? '#ffffff' : 'var(--au-text2)' }}
    >
      <span className="text-[7.5px] font-bold whitespace-nowrap">{label}</span>
      {extra && <span className="text-[7.5px] font-medium whitespace-nowrap opacity-90">{extra}</span>}
    </span>
  )
}

/**
 * The client cell of a planning-board row: an optional tree elbow for a subsidiary, the client name
 * in accent, and its legal-form tag. Shared by every board so a subsidiary is indented by exactly
 * the same amount everywhere — and by the same amount the sub-header's row label starts at.
 */
export function BoardName({
  nr,
  indent,
  size = 'card',
  showNr,
  showTag = true,
}: {
  nr: ClientNr
  /** Renders the row one level in, under its parent holding. */
  indent?: boolean
  size?: 'hero' | 'card'
  /** Shows the client number before the name (month boards do, the hero does not). */
  showNr?: boolean
  /** Shows the legal-form tag. Off on the tightest boards, where it is just noise. */
  showTag?: boolean
}) {
  const { name, form } = clientLookup(nr)
  return (
    <div className={clsx(styles.boardName, size === 'hero' ? styles.boardNameHero : styles.boardNameCard)}>
      {indent && <span className={styles.boardIndent} aria-hidden="true" />}
      {showNr && <span className={styles.boardNr}>{nr}</span>}
      <span className={styles.boardNameText}>{name}</span>
      {showTag && <Tag className="shrink-0">{form}</Tag>}
    </div>
  )
}

/**
 * A planning-board status cell. Colour, label and shape all come from the shared status registry in
 * `demo-data`, so the hero, every month board and the pop-up badges cannot disagree about what a
 * "Loopt" looks like.
 */
export type PillTone = StatusKey

export function PlanningPill({
  tone,
  label,
  ringed,
  size = 'card',
  className,
  cellRef,
}: {
  tone: PillTone
  /** Overrides the status label — month boards show the responsible colleague's initials instead. */
  label?: ReactNode
  ringed?: boolean
  size?: 'hero' | 'card'
  className?: string
  cellRef?: React.RefObject<HTMLSpanElement | null>
}) {
  const { t } = useVisualCopy()
  const status = PLANNING_STATUS[tone]
  return (
    <span
      ref={cellRef}
      className={clsx(
        styles.pill,
        size === 'hero' ? styles.pillHero : styles.pillCard,
        status.shape === 'dashed' && styles.pillDashed,
        status.shape === 'empty' && styles.pillEmpty,
        ringed && styles.pillRinged,
        className,
      )}
      style={status.color ? { background: status.color, color: '#ffffff' } : undefined}
    >
      {label ?? t.status[tone]}
    </span>
  )
}

/** Status badge for pop-ups and detail panes — same words and colours as the board cells. */
export function StatusBadge({ status, className }: { status: StatusKey; className?: string }) {
  const { t } = useVisualCopy()
  const spec = PLANNING_STATUS[status]
  return (
    <span
      className={clsx(styles.statusBadge, !spec.color && styles.statusBadgeOutlined, className)}
      style={spec.color ? { background: spec.color, color: '#ffffff' } : undefined}
    >
      {t.status[status]}
    </span>
  )
}

/** The small uppercase caption above a field. Use everywhere a field is labeled. */
export function FieldLabel({ children, roomy }: { children: ReactNode; roomy?: boolean }) {
  return <span className={clsx(styles.fieldLabel, roomy && styles.fieldLabelRoomy)}>{children}</span>
}

/**
 * The field styling as raw class names, for the few inputs that cannot use `Field` because they
 * carry extra chrome (a typing caret, an autocomplete dropdown). Applying `box` — and `filled` once
 * a value lands — keeps those inputs identical to every `Field` on the site, flash included.
 */
export const fieldClasses = {
  label: styles.fieldLabel,
  box: styles.field,
  filled: styles.fieldFilled,
  /** Roomier scale, for a dialog that fills a card on its own. */
  boxRoomy: styles.fieldRoomy,
  labelRoomy: styles.fieldLabelRoomy,
} as const

/**
 * Labeled form field, filled (value) or empty (placeholder, muted text2) end-state.
 *
 * Whenever the value changes it briefly flashes accent-soft, so a field filling itself in reads as
 * "this was just entered" rather than silently changing. The `key` on the value box is what replays
 * the animation: a new value remounts the node, restarting the keyframes.
 */
export function Field({
  label,
  value,
  placeholder,
  bold,
  align,
  roomy,
  className,
}: {
  label: string
  value?: ReactNode
  placeholder?: ReactNode
  bold?: boolean
  align?: 'left' | 'right'
  /** Roomier scale, for a dialog or panel that fills a card on its own — matches `FieldLabel`. */
  roomy?: boolean
  className?: string
}) {
  return (
    <div className={clsx('flex min-w-0 flex-col gap-0.5', className)}>
      <span className={clsx(styles.fieldLabel, roomy && styles.fieldLabelRoomy)}>{label}</span>
      <div
        key={value === undefined || value === null ? '' : String(value)}
        className={clsx(
          styles.field,
          roomy && styles.fieldRoomy,
          value != null && value !== '' && styles.fieldFilled,
          align === 'right' && 'text-right',
        )}
        style={{
          color: value ? (bold ? 'var(--au-accent)' : 'var(--au-text)') : 'var(--au-text2)',
          fontWeight: bold && value ? 700 : 500,
        }}
      >
        {value ?? placeholder}
      </div>
    </div>
  )
}

/**
 * Small pill tag — entity type and the like.
 *
 * It removes itself on a narrow card. Five of these competing with the client names they belong to,
 * in a column that has usually given up half its width by then, is not a sizing problem: the name
 * is what the row is about and the legal form is not worth the space it takes there.
 */
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        styles.tag,
        'rounded-[4px] px-1 py-[1px] text-[7px] font-bold whitespace-nowrap text-white uppercase',
        className,
      )}
      style={{ background: 'var(--au-tag)', letterSpacing: '0.02em' }}
    >
      {children}
    </span>
  )
}

// Avatar colours come from the shared team list, so a colleague keeps the same colour everywhere.

/** Overlapping avatar-initials stack (team members on a client row, etc). */
export function AvatarStack({ initials, className }: { initials: string[]; className?: string }) {
  return (
    <div className={clsx('flex items-center', className)}>
      {initials.map((initials2, i) => (
        <span
          key={`${initials2}-${i}`}
          className="flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 text-[8.5px] font-bold text-white"
          style={{
            background: AVATAR_COLORS[initials2] ?? 'var(--au-tag)',
            borderColor: 'var(--au-surface)',
            marginLeft: i === 0 ? 0 : -7,
          }}
        >
          {initials2}
        </span>
      ))}
    </div>
  )
}
